import api from './api';
import { FarmerPdfReportListResponse } from '../types/drisReport';

interface PdfGenerationRequest {
  htmlContent: string;
  fileName: string;
  metadata?: {
    batchId: number;
    batchNumber: string;
    farmer: string;
    farm: string;
    cropType: string;
    selectedPlot: string;
    generatedAt: string;
  };
}

interface PdfGenerationResponse {
  jobId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  message?: string;
  createdAt: string;
}

interface PdfJobStatus {
  jobId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress?: number;
  fileName?: string;
  downloadUrl?: string;
  error?: string;
  createdAt: string;
  updatedAt: string;
  expiresAt?: string;
}

class PdfGenerationService {
  // MIGRATED: Changed from '/pdf-generation' to new secure endpoint
  private baseUrl = '/dris-pdf-reports';

  /**
   * Submit a new PDF generation job
   */
  async submitPdfJob(request: PdfGenerationRequest): Promise<PdfGenerationResponse> {
    try {
      // Extract batchId from metadata if available
      const batchId = request.metadata?.batchId;
      if (!batchId) {
        throw new Error('Batch ID is required for PDF generation');
      }

      // Use new endpoint format: /dris-pdf-reports/generate/{batchId}
      const response = await api.post<PdfGenerationResponse>(
        `${this.baseUrl}/generate/${batchId}`,
        { htmlContent: request.htmlContent } // Send only HTML content in body
      );
      return response.data;
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { message?: string } }; message?: string };
      throw new Error(axiosError.response?.data?.message || 'Failed to submit PDF generation job');
    }
  }

  /**
   * Get the status of a PDF generation job
   */
  async getPdfJobStatus(jobId: string): Promise<PdfJobStatus> {
    try {
      const response = await api.get<PdfJobStatus>(
        `${this.baseUrl}/status/${jobId}`
      );
      return response.data;
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { message?: string } }; message?: string };
      throw new Error(axiosError.response?.data?.message || 'Failed to get PDF job status');
    }
  }

  /**
   * Download a generated PDF
   */
  async downloadPdf(jobId: string): Promise<Blob> {
    try {
      const response = await api.get(
        `${this.baseUrl}/download/${jobId}`,
        {
          responseType: 'blob',
          timeout: 300000 // 5 minutes for large PDF downloads
        }
      );
      return response.data;
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { message?: string } }; message?: string };
      throw new Error(axiosError.response?.data?.message || 'Failed to download PDF');
    }
  }

  /**
   * Cancel a PDF generation job
   */
  async cancelPdfJob(jobId: string): Promise<void> {
    try {
      await api.delete(`${this.baseUrl}/cancel/${jobId}`);
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { message?: string } }; message?: string };
      throw new Error(axiosError.response?.data?.message || 'Failed to cancel PDF job');
    }
  }

  /**
   * Get all PDF jobs for a batch
   */
  async getBatchPdfJobs(batchId: number): Promise<PdfJobStatus[]> {
    try {
      const response = await api.get<PdfJobStatus[]>(
        `${this.baseUrl}/batch/${batchId}`
      );
      return response.data;
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { message?: string } }; message?: string };
      throw new Error(axiosError.response?.data?.message || 'Failed to get batch PDF jobs');
    }
  }

  /**
   * Generate a direct download URL for a completed PDF
   */
  getDownloadUrl(jobId: string): string {
    return `${api.defaults.baseURL}${this.baseUrl}/download/${jobId}`;
  }

  /**
   * Get all PDF reports for a farmer (lightweight summaries)
   */
  async getFarmerPdfReports(farmerId: number): Promise<FarmerPdfReportListResponse> {
    const response = await api.get(`${this.baseUrl}/farmer/${farmerId}`);
    return response.data.data;  // Unwrap ApiResponse envelope
  }

  /**
   * Check if PDF generation service is available
   */
  async checkServiceHealth(): Promise<boolean> {
    try {
      const response = await api.get(`${this.baseUrl}/health`);
      return response.status === 200;
    } catch {
      return false;
    }
  }
}

export const pdfGenerationService = new PdfGenerationService();